/**
 * Master-solution extraction. One implementation for every trigger: the first extraction after the
 * structure was confirmed, and every later re-upload. Both used to be separate ~240-line copies that
 * had drifted apart (different "is this usable" counting, one wiping needsAnswer, one not).
 */

import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam } from '../../exam.js';
import { clearSolutionTasks, setSolutionStatus, startSolutionProgress, StepState } from '../../extraction-progress.js';
import { bbxKind } from '../agents/bbx-kind.js';
import { runBoundingBoxAgent } from '../agents/extraction-modules/bounding-box.js';
import { buildExamTasksFromRecursiveStructure } from '../agents/extraction-modules/build-exam-tasks.js';
import { enrichTasksWithOCRByBbx, parseAgentAnswer } from '../agents/extraction-modules/task-enrichment.js';
import {
  countExtractedFieldEntries,
  countSolutionUnitNodes,
  createCostTracker,
  loadStepInput,
  persistExtractedArtifact,
  SKIPPED,
  type StepResult,
} from './step-utils.js';

const CONTEXT = 'MS-Extraktion';

/**
 * Reads the uploaded master solution into the exam's tasks. Requires a task structure to exist — the
 * bounding-box agent is told which tasks to look for, so there is nothing to do without them.
 */
export async function runMasterSolutionStep(examId: string): Promise<StepResult> {
  const input = await loadStepInput(examId);
  if (!input) return { ok: false, skipped: false };

  const masterSolutionIds: string[] = Array.isArray(input.exam.masterSolutionIds) ? input.exam.masterSolutionIds : [];
  if (masterSolutionIds.length === 0) {
    logger.info({ examId, context: CONTEXT }, 'Keine Musterlösung hochgeladen — Schritt übersprungen');
    return SKIPPED;
  }
  if (input.taskRecords.length === 0) {
    logger.error({ examId, context: CONTEXT }, 'Keine Aufgabenstruktur vorhanden — Extraktion nicht möglich');
    return { ok: false, skipped: false };
  }

  const examCollection = await getCollection(Exam as any);
  const cost = createCostTracker(examId);
  let failed = false;

  // A previous attempt's upload error is cleared here, so the step is self-contained no matter which
  // caller triggered it. That a master solution was uploaded is recorded by `masterSolutionIds` itself.
  await examCollection.updateOne({ _id: examId } as any, {
    $unset: { 'examState.masterSolutionError': '' },
  });

  try {
    logger.info({ examId, imageCount: masterSolutionIds.length, context: CONTEXT }, 'Starte Extraktion');
    // This run re-reads every task, so whatever a previous run (or a generation) counted as done is
    // dropped first — the count that follows describes this extraction.
    await clearSolutionTasks(examId, 'masterSolution');
    await startSolutionProgress(examId, 'masterSolution', {
      producer: 'extraction',
      total: countSolutionUnitNodes(input.taskRecords),
      message: 'progress.extractingMasterSolution',
    });
    const bbxResult = await runBoundingBoxAgent(masterSolutionIds, bbxKind.MS, input.minimalTasksJson);
    cost.addCost(bbxResult?.cost, 'masterSolutionBoundingBox');
    if (!bbxResult) throw new Error('Master solution BBX detection returned no results');

    const rawAnswer = typeof bbxResult === 'string' ? bbxResult : bbxResult?.answer;
    if (!rawAnswer) throw new Error('No regions found in master solution BBX result');

    const parsed = parseAgentAnswer(bbxResult);
    logger.info({ examId, context: CONTEXT }, 'BBX-Ergebnis geparst, starte OCR-Anreicherung');

    const enrichedTasks = await enrichTasksWithOCRByBbx(
      input.taskRecords,
      parsed,
      masterSolutionIds,
      'masterSolutionContent',
      false,
      c => cost.addCost(c, 'masterSolutionOcr'),
      examId,
      'masterSolution',
    );

    const extractedCount = countExtractedFieldEntries(enrichedTasks || input.taskRecords, 'masterSolutionContent');
    if (extractedCount <= 0) {
      logger.error({ examId, context: CONTEXT }, 'Keine verwertbaren Master-Solution-Inhalte extrahiert');
      await setSolutionStatus(examId, 'masterSolution', StepState.ERROR, 'progress.noUsableMasterSolution');
      await cost.flush();
      return { ok: false, skipped: false };
    }
    logger.info({ examId, extractedCount, context: CONTEXT }, 'OCR-Anreicherung abgeschlossen');

    try {
      // Never re-seed own points here: the teacher confirmed them at structure time and
      // examTaskTreeFromDB carries them forward. Only the structure step seeds the one-time guess.
      const examTasks = await buildExamTasksFromRecursiveStructure(enrichedTasks || input.taskRecords, examId);

      // Only the `masterSolution` field of each task, so a grading-scheme extraction can run alongside this
      // one instead of the two overwriting each other's rebuilt tree. See `persistExtractedArtifact`, including
      // why the fixed parent totals are the grading scheme's to drop and not this step's.
      await persistExtractedArtifact(examId, examTasks, 'masterSolution');
      logger.info({ examId, taskCount: examTasks.length, context: CONTEXT }, 'Tasks gespeichert');
    } catch (err) {
      logger.error({ err, examId, context: CONTEXT }, 'Fehler beim Erstellen/Mergen von Tasks');
      await setSolutionStatus(examId, 'masterSolution', StepState.ERROR, 'progress.errorStructureMasterSolution');
      await cost.flush();
      return { ok: false, skipped: false };
    }
  } catch (err) {
    logger.error({ err, examId, context: CONTEXT }, 'KRITISCHER FEHLER bei Master-Solution-Extraktion');
    failed = true;
  }

  await setSolutionStatus(
    examId,
    'masterSolution',
    failed ? StepState.ERROR : StepState.COMPLETED,
    failed ? 'progress.errorExtractingMasterSolution' : 'progress.masterSolutionExtracted',
  );
  await cost.flush();

  return { ok: !failed, skipped: false };
}
